/**
 * Carte écrite par l'utilisateur et cartes qu'elle génère (spécification §4, ADR 0005).
 *
 * Dans le modèle de données, toute carte écrite est une carte mère, même quand son mode ne
 * génère qu'une carte. Chaque carte générée est identifiée dans sa carte mère par une clé
 * stable : modifier la carte mère ne touche pas au suivi FSRS des cartes inchangées.
 */
import { numeroterTrous, numerosDesTrous, analyserTrous } from './trous';

export type TypeCarte = 'basique' | 'trous' | 'image' | 'tableau';

/** Basique : case « inverse facultative » décochée ou cochée. */
export type ModeBasique = 'sans_inverse' | 'avec_inverse';
/** Texte à trous : un trou par carte, ou tout masquer. */
export type ModeTrous = 'un_par_carte' | 'tout_masquer';
/** Masquage d'image ou de tableau : une zone (case) par carte, ou tout masquer. */
export type ModeMasquage =
  'un_par_carte' | 'tout_masquer_tout_demander' | 'tout_masquer_une_demandee';

export interface ContenuBasique {
  recto: string;
  verso: string;
}

export interface ContenuTrous {
  texte: string;
}

/** Zone rectangulaire, en fraction de la largeur et de la hauteur de l'image (0 à 1). */
export interface Zone {
  id: string;
  x: number;
  y: number;
  largeur: number;
  hauteur: number;
}

export interface ContenuImage {
  /** Chemin de l'image dans le stockage (`<utilisateur>/<fichier>`). */
  image: string;
  zones: Zone[];
}

export interface CaseTableau {
  id: string;
  /** Contenu mis en forme (Markdown). */
  contenu: string;
  /** Case grisée : à masquer. */
  grisee: boolean;
}

export interface ContenuTableau {
  lignes: CaseTableau[][];
}

export type CarteMere =
  | { type: 'basique'; mode: ModeBasique; contenu: ContenuBasique }
  | { type: 'trous'; mode: ModeTrous; contenu: ContenuTrous }
  | { type: 'image'; mode: ModeMasquage; contenu: ContenuImage }
  | { type: 'tableau'; mode: ModeMasquage; contenu: ContenuTableau };

export const MODES: { [T in TypeCarte]: readonly Extract<CarteMere, { type: T }>['mode'][] } = {
  basique: ['sans_inverse', 'avec_inverse'],
  trous: ['un_par_carte', 'tout_masquer'],
  image: ['un_par_carte', 'tout_masquer_tout_demander', 'tout_masquer_une_demandee'],
  tableau: ['un_par_carte', 'tout_masquer_tout_demander', 'tout_masquer_une_demandee'],
};

/** Mode proposé tant que l'utilisateur n'en a choisi aucun pour ce type. */
export const MODES_PAR_DEFAUT: { [T in TypeCarte]: Extract<CarteMere, { type: T }>['mode'] } = {
  basique: 'sans_inverse',
  trous: 'un_par_carte',
  image: 'un_par_carte',
  tableau: 'un_par_carte',
};

/** Clé d'une carte qui demande tout (mode « tout masquer »). */
export const CLE_TOUT = 'tout';
const IDENTIFIANT = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Les cartes filles sont repoussées au lendemain (une par carte mère et par jour)
 * uniquement quand l'une peut dévoiler la réponse d'une autre (spécification §4).
 */
export function reportAuLendemain(carte: Pick<CarteMere, 'type' | 'mode'>): boolean {
  return carte.mode === 'avec_inverse' || carte.mode === 'un_par_carte';
}

/** Numérote les trous saisis sans numéro. Les autres types sont rendus tels quels. */
export function normaliserCarteMere(carte: CarteMere): CarteMere {
  if (carte.type !== 'trous') return carte;
  return { ...carte, contenu: { ...carte.contenu, texte: numeroterTrous(carte.contenu.texte) } };
}

function casesGrisees(contenu: ContenuTableau): CaseTableau[] {
  return contenu.lignes.flat().filter((cellule) => cellule.grisee);
}

/**
 * Clés des cartes filles, dans l'ordre de présentation au sein de la carte mère :
 * `recto`, `verso` (sens inverse), `trou:<n>`, `zone:<id>`, `case:<id>`, ou `tout`.
 */
export function clesCartesFilles(carte: CarteMere): string[] {
  switch (carte.type) {
    case 'basique':
      return carte.mode === 'avec_inverse' ? ['recto', 'verso'] : ['recto'];
    case 'trous': {
      const numeros = numerosDesTrous(numeroterTrous(carte.contenu.texte));
      if (numeros.length === 0) return [];
      return carte.mode === 'tout_masquer' ? [CLE_TOUT] : numeros.map((n) => `trou:${n}`);
    }
    case 'image': {
      const zones = carte.contenu.zones;
      if (zones.length === 0) return [];
      return carte.mode === 'tout_masquer_tout_demander'
        ? [CLE_TOUT]
        : zones.map((zone) => `zone:${zone.id}`);
    }
    case 'tableau': {
      const cases = casesGrisees(carte.contenu);
      if (cases.length === 0) return [];
      return carte.mode === 'tout_masquer_tout_demander'
        ? [CLE_TOUT]
        : cases.map((cellule) => `case:${cellule.id}`);
    }
  }
}

function identifiantsValides(ids: string[], nom: string): string[] {
  const erreurs: string[] = [];
  if (ids.some((id) => !IDENTIFIANT.test(id))) erreurs.push(`Identifiant de ${nom} invalide.`);
  if (new Set(ids).size !== ids.length) erreurs.push(`Deux ${nom}s ont le même identifiant.`);
  return erreurs;
}

/** Vérifie une carte mère avant enregistrement. Renvoie les erreurs (vide si valide). */
export function validerCarteMere(carte: CarteMere): string[] {
  if (!(MODES[carte.type] as readonly string[]).includes(carte.mode)) {
    return [`Mode « ${carte.mode} » inconnu pour ce type de carte.`];
  }
  const erreurs: string[] = [];
  switch (carte.type) {
    case 'basique':
      if (carte.contenu.recto.trim() === '') erreurs.push('Le recto est vide.');
      if (carte.mode === 'avec_inverse' && carte.contenu.verso.trim() === '') {
        erreurs.push('Le verso est vide : il sert de question à la carte inverse.');
      }
      break;
    case 'trous': {
      const trous = analyserTrous(carte.contenu.texte);
      if (trous.length === 0) erreurs.push('Le texte ne contient aucun trou {{…}}.');
      if (trous.some((trou) => trou.reponse.trim() === '')) erreurs.push('Un trou est vide.');
      break;
    }
    case 'image': {
      const { image, zones } = carte.contenu;
      if (image.trim() === '') erreurs.push('Aucune image.');
      if (zones.length === 0) erreurs.push('Aucune zone de masquage.');
      erreurs.push(
        ...identifiantsValides(
          zones.map((zone) => zone.id),
          'zone',
        ),
      );
      const horsImage = (zone: Zone) =>
        ![zone.x, zone.y, zone.largeur, zone.hauteur].every(Number.isFinite) ||
        zone.x < 0 ||
        zone.y < 0 ||
        zone.largeur <= 0 ||
        zone.hauteur <= 0 ||
        zone.x + zone.largeur > 1 ||
        zone.y + zone.hauteur > 1;
      if (zones.some(horsImage)) erreurs.push('Une zone dépasse de l’image.');
      break;
    }
    case 'tableau': {
      const cases = carte.contenu.lignes.flat();
      if (casesGrisees(carte.contenu).length === 0) erreurs.push('Aucune case grisée.');
      erreurs.push(
        ...identifiantsValides(
          cases.map((cellule) => cellule.id),
          'case',
        ),
      );
      break;
    }
  }
  return erreurs;
}

/**
 * Effet d'une modification sur les cartes filles existantes : l'interface peut prévenir
 * avant de supprimer des cartes et leur historique.
 */
export function differenceCartesFilles(
  existantes: readonly string[],
  nouvelles: readonly string[],
): { aCreer: string[]; aSupprimer: string[]; conservees: string[] } {
  const avant = new Set(existantes);
  const apres = new Set(nouvelles);
  return {
    aCreer: nouvelles.filter((cle) => !avant.has(cle)),
    aSupprimer: existantes.filter((cle) => !apres.has(cle)),
    conservees: nouvelles.filter((cle) => avant.has(cle)),
  };
}
